import React, { useState, useEffect, useRef } from 'react';
import { X, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { ventaActividadService } from '../services/ventaActividadService';
import { productoActividadService } from '../services/productoActividadService';
import { produccionService, ProduccionConMetricas } from '../services/produccionService';
import { ProductoActividad, VentaActividad, Miembro } from '../lib/supabase';
import { validarCliente, limpiarCamposCliente } from '../utils/clienteHelpers';
import { useLumisial } from '../context/LumisialContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { DialogShell } from './ui/DialogShell';
import { Paso1ClienteProducto } from './pedidos/Paso1ClienteProducto';
import { Paso2EntregaPago } from './pedidos/Paso2EntregaPago';
import { formatMoney } from './ui/MoneyInput';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { comprobanteService } from '../services/comprobanteService';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import type { EntidadDinero } from '../types/comprobante';
import { validarImportesVentaActividad } from '../utils/validacionImportesActividad';
import { fechaEnChile } from '../utils/dateHelpers';
import { fechasAlAvanzarPedidoActividad, prepararCambiosPedidoActividad, validarStockLotePedidoActividad } from '../utils/edicionPedidoActividad';

interface VentaActividadModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly actividadId: string;
  readonly venta?: VentaActividad;
  readonly miembros: Miembro[];
  readonly sinAsociacionMiembro?: boolean;
  /** 'nested' cuando se abre sobre otra ventana ya abierta (ej. el detalle de pedido). */
  readonly level?: 'base' | 'nested';
}

function buildFormDataFromVenta(venta: VentaActividad, precioUnitario: number) {
  return {
    producto_id: venta.producto_id || '',
    produccion_id: venta.produccion_id || '',
    miembro_id: venta.miembro_id || '',
    nombre_externo: venta.nombre_externo || '',
    miembro_referido_id: venta.miembro_referido_id || '',
    cantidad: venta.cantidad,
    precio_unitario: precioUnitario,
    fecha_venta: venta.fecha_venta.split('T')[0],
    forma_entrega: venta.forma_entrega,
    fecha_entrega: venta.fecha_entrega?.split('T')[0] || '',
    direccion_despacho: venta.direccion_despacho || '',
    rango_horario_retiro_despacho: venta.rango_horario_retiro_despacho || '',
    notas: venta.notas || '',
    registrar_pago: false,
    monto_pago_inicial: venta.monto_pagado,
    fecha_pago: venta.fecha_pago_inicial?.split('T')[0] || '',
    registrar_entrega: false,
    cantidad_entrega_inicial: venta.cantidad_entregada,
    fecha_entrega_real: venta.fecha_entrega_inicial?.split('T')[0] || ''
  };
}

function buildEmptyFormData(productoIdAuto: string, precioUnitarioAuto: number, produccionIdAuto: string) {
  return {
    producto_id: productoIdAuto,
    produccion_id: produccionIdAuto,
    miembro_id: '',
    nombre_externo: '',
    miembro_referido_id: '',
    cantidad: 1,
    precio_unitario: precioUnitarioAuto,
    fecha_venta: fechaEnChile(new Date()) ?? '',
    forma_entrega: 'retiro' as 'retiro' | 'despacho',
    fecha_entrega: fechaEnChile(new Date()) ?? '',
    direccion_despacho: '',
    rango_horario_retiro_despacho: '',
    notas: '',
    registrar_pago: false,
    monto_pago_inicial: 0,
    fecha_pago: fechaEnChile(new Date()) ?? '',
    registrar_entrega: false,
    cantidad_entrega_inicial: 0,
    fecha_entrega_real: fechaEnChile(new Date()) ?? ''
  };
}

export const VentaActividadModal: React.FC<VentaActividadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  actividadId,
  venta,
  miembros,
  sinAsociacionMiembro = false,
  level = 'base'
}) => {
  const { selectedLumisial } = useLumisial();
  const [loading, setLoading] = useState(false);
  const [productos, setProductos] = useState<ProductoActividad[]>([]);
  const [producciones, setProducciones] = useState<ProduccionConMetricas[]>([]);
  const [pasoActual, setPasoActual] = useState(1);
  const formRef = useRef<HTMLFormElement>(null);

  // Estado para miembro y miembro referido seleccionados (para pasar al ComboboxLazy)
  const [miembroSeleccionado, setMiembroSeleccionado] = useState<{ id: string; nombre: string } | null>(null);
  const [miembroReferidoSeleccionado, setMiembroReferidoSeleccionado] = useState<{ id: string; nombre: string } | null>(null);

  // Calcular nombres dinámicamente basados en formData
  const initialMiembroNombre = miembroSeleccionado?.nombre || (venta?.miembro_id 
    ? miembros.find(m => m.id === venta.miembro_id)?.nombres + ' ' + miembros.find(m => m.id === venta.miembro_id)?.apellidos
    : undefined);
  
  const initialMiembroReferidoNombre = miembroReferidoSeleccionado?.nombre || (venta?.miembro_referido_id
    ? miembros.find(m => m.id === venta.miembro_referido_id)?.nombres + ' ' + miembros.find(m => m.id === venta.miembro_referido_id)?.apellidos
    : undefined);

  // Hook centralizado para manejo de errores
  const { error, clearError, handleError } = useErrorHandler();

  const [tipoCliente, setTipoCliente] = useState<'miembro' | 'externo'>('miembro');
  const [formData, setFormData] = useState({
    producto_id: '',
    produccion_id: '',
    miembro_id: '',
    nombre_externo: '',
    miembro_referido_id: '',
    cantidad: 1,
    precio_unitario: 0,
    fecha_venta: fechaEnChile(new Date()) ?? '',
    forma_entrega: 'retiro' as 'retiro' | 'despacho',
    fecha_entrega: '',
    direccion_despacho: '',
    rango_horario_retiro_despacho: '',
    notas: '',
    registrar_pago: false,
    monto_pago_inicial: 0,
    fecha_pago: fechaEnChile(new Date()) ?? '',
    registrar_entrega: false,
    cantidad_entrega_inicial: 0,
    fecha_entrega_real: fechaEnChile(new Date()) ?? ''
  });
  const [initialFormData, setInitialFormData] = useState(formData);

  // Entidad dueña de la actividad: es donde queda guardado el comprobante del pago inicial.
  const [entidad, setEntidad] = useState<EntidadDinero | null>(null);

  // Al cambiar de actividad con el modal montado, lo cargado de la anterior no puede quedar a la vista:
  // se descarta en el render y las cargas de la actividad nueva lo reemplazan.
  const [actividadCargada, setActividadCargada] = useState(actividadId);
  if (actividadCargada !== actividadId) {
    setActividadCargada(actividadId);
    setProductos([]);
    setProducciones([]);
    setEntidad(null);
    setFormData(buildEmptyFormData('', 0, ''));
    setInitialFormData(buildEmptyFormData('', 0, ''));
    setPasoActual(1);
    setMiembroSeleccionado(null);
    setMiembroReferidoSeleccionado(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    let vigente = true;
    comprobanteService.getEntidadDeActividad(actividadId)
      .then((dueña) => { if (vigente) setEntidad(dueña); })
      .catch((err: unknown) => { if (vigente) reportarErrorInesperado(err, 'No pudimos preparar el comprobante del pago.'); });
    return () => { vigente = false; };
  }, [isOpen, actividadId]);

  // El comprobante del pago inicial solo se pide al registrar un pedido nuevo.
  const pideComprobanteDelPago = !venta && formData.registrar_pago && formData.monto_pago_inicial > 0;
  const comprobante = useComprobanteFormulario({
    entidad,
    fechaMovimiento: formData.fecha_pago,
    permiteSubirDespues: true,
    pideMedioPago: true,
    sentidoDinero: 'ingreso',
    obligatoriedad: pideComprobanteDelPago ? 'desde_vigencia' : 'nunca',
    medioPagoInicial: venta?.modo_pago ?? 'efectivo',
  });
  const { reiniciar: reiniciarComprobante } = comprobante;
  useEffect(() => {
    if (isOpen) reiniciarComprobante();
  }, [isOpen, venta?.id, reiniciarComprobante]);

  // Estado inicial para comparar cambios
  // Función para detectar cambios en el formulario
  const hasFormChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData) || comprobante.hayCambios;
  };

  // Actualizar estado inicial después de guardar exitosamente
  useEffect(() => {
    if (!loading && !error && !isOpen) {
      // Modal cerró después de guardar exitosamente
      setInitialFormData(formData);
    }
  }, [loading, error, isOpen]);

  // Establecer estado inicial cuando se carga el modal (solo la primera vez que abre)
  useEffect(() => {
    if (isOpen) {
      // Establecer estado inicial con los datos limpios del modal
      if (venta) {
        const precioUnitario = venta.producto_id && productos.length > 0
          ? productos.find(p => p.id === venta.producto_id)?.precio_unitario || 0
          : Number(venta.monto_total) / venta.cantidad;

        const cleanFormData = buildFormDataFromVenta(venta, precioUnitario);
        setInitialFormData(cleanFormData);
      } else {
        // Auto-seleccionar el único producto si solo hay uno disponible
        const productoIdAuto = productos.length === 1 ? productos[0].id : '';
        const precioUnitarioAuto = productos.length === 1 ? productos[0].precio_unitario : 0;
        const cleanFormData = buildEmptyFormData(productoIdAuto, precioUnitarioAuto, productoIdAuto ? seleccionarLoteInteligente(productoIdAuto) : '');
        setInitialFormData(cleanFormData);
      }
    }
  }, [isOpen, venta, productos, producciones]);

  // El aviso de cambios sin guardar lo gestiona DialogShell (hasUnsavedChanges se calcula tras el early return)

  useEffect(() => {
    let autofocusTimer: ReturnType<typeof setTimeout> | undefined;
    let vigente = true;
    if (isOpen) {
      void loadProductos(() => vigente);
      document.body.style.overflow = 'hidden';
      // Autofocus al primer campo interactivo del formulario
      autofocusTimer = setTimeout(() => {
        if (formRef.current) {
          const firstInput = formRef.current.querySelector<HTMLElement>(
            'select:not([disabled]), input:not([disabled]):not([type="radio"]):not([type="checkbox"]), [tabindex="0"]'
          );
          firstInput?.focus();
        }
      }, 100);
    }

    return () => {
      vigente = false;
      clearTimeout(autofocusTimer);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, actividadId]);

  useEffect(() => {
    if (venta) {
      const precioUnitario = venta.producto_id && productos.length > 0
        ? productos.find(p => p.id === venta.producto_id)?.precio_unitario || 0
        : Number(venta.monto_total) / venta.cantidad;

      setTipoCliente(venta.miembro_id ? 'miembro' : 'externo');
      const newFormData = buildFormDataFromVenta(venta, precioUnitario);
      setFormData(newFormData);
      setInitialFormData(newFormData);
    } else {
      setTipoCliente(sinAsociacionMiembro ? 'externo' : 'miembro');

      // Auto-seleccionar el único producto si solo hay uno disponible
      const productoIdAuto = productos.length === 1 ? productos[0].id : '';
      const precioUnitarioAuto = productos.length === 1 ? productos[0].precio_unitario : 0;
      const newFormData = buildEmptyFormData(productoIdAuto, precioUnitarioAuto, productoIdAuto ? seleccionarLoteInteligente(productoIdAuto) : '');
      setFormData(newFormData);
      setInitialFormData(newFormData);
    }
    clearError();
  }, [venta, isOpen, productos, producciones, sinAsociacionMiembro]);

  const loadProductos = async (vigente: () => boolean) => {
    try {
      const [productosData, produccionesData] = await Promise.all([
        productoActividadService.getByActividad(actividadId),
        produccionService.getByActividadConMetricas(actividadId)
      ]);
      if (!vigente()) return;
      setProductos(productosData);
      setProducciones(produccionesData);
    } catch (err) {
      if (vigente()) reportarErrorInesperado(err, 'No pudimos cargar los productos.');
    }
  };

  // Selección inteligente de lote:
  // 1. Si solo hay 1 lote activo → seleccionarlo
  // 2. Si hay varios, filtrar los que tienen stock > 0
  // 3. Si solo 1 tiene stock → seleccionarlo
  // 4. Si varios tienen stock → seleccionar el más antiguo (menor número)
  // 5. Si ninguno tiene stock → no seleccionar
  const seleccionarLoteInteligente = (productoId: string): string => {
    const lotesActivos = producciones.filter(p => p.producto_id === productoId && p.estado === 'activa');
    if (lotesActivos.length === 0) return '';
    if (lotesActivos.length === 1) return lotesActivos[0].id;
    const lotesConStock = lotesActivos.filter(l => l.stock_disponible > 0);
    if (lotesConStock.length === 0) return '';
    if (lotesConStock.length === 1) return lotesConStock[0].id;
    lotesConStock.sort((a, b) => a.numero - b.numero);
    return lotesConStock[0].id;
  };

  const handleProductoChange = (productoId: string) => {
    const producto = productos.find(p => p.id === productoId);
    setFormData({
      ...formData,
      producto_id: productoId,
      produccion_id: seleccionarLoteInteligente(productoId),
      precio_unitario: producto?.precio_unitario || 0
    });
  };

  const handleProduccionChange = (produccionId: string) => {
    setFormData({ ...formData, produccion_id: produccionId });
  };

  // Validación del Paso 1
  const validarPaso1 = (): boolean => {
    if (!formData.producto_id) {
      handleError('Debes seleccionar un producto');
      return false;
    }

    const errorCliente = validarCliente(tipoCliente, formData.miembro_id, formData.nombre_externo);
    if (errorCliente) {
      handleError(errorCliente);
      return false;
    }

    if (formData.cantidad <= 0) {
      handleError('La cantidad debe ser mayor a 0');
      return false;
    }

    // Validar stock disponible del lote seleccionado
    if (formData.produccion_id) {
      const lote = producciones.find(p => p.id === formData.produccion_id);
      const errorStock = validarStockLotePedidoActividad(formData.cantidad,
        formData.producto_id, formData.produccion_id, lote, venta);
      if (errorStock) { handleError(errorStock); return false; }
    }
    return true;
  };

  // Navegación entre pasos
  const irAlPaso2 = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (validarPaso1()) {
      clearError();
      setFormData(prev => fechasAlAvanzarPedidoActividad(prev, !!venta));
      setPasoActual(2);
      // Autofocus al primer campo del paso 2
      setTimeout(() => {
        if (formRef.current) {
          const firstInput = formRef.current.querySelector<HTMLElement>(
            'input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex="0"]'
          );
          firstInput?.focus();
        }
      }, 100);
    }
  };

  const volverAlPaso1 = () => {
    clearError();
    setPasoActual(1);
    // Autofocus al primer campo del paso 1
    setTimeout(() => {
      if (formRef.current) {
        const firstInput = formRef.current.querySelector<HTMLElement>(
          'select:not([disabled]), input:not([disabled]):not([type="radio"]):not([type="checkbox"]), [tabindex="0"]'
        );
        firstInput?.focus();
      }
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Si estamos en paso 1, avanzar al paso 2 (Enter = botón Siguiente)
    if (pasoActual === 1) {
      irAlPaso2();
      return;
    }

    if (!validarPaso1()) return;

    if (formData.registrar_pago && formData.monto_pago_inicial <= 0) {
      handleError('El monto de pago debe ser mayor a 0');
      return;
    }

    if (formData.registrar_entrega && formData.cantidad_entrega_inicial <= 0) {
      handleError('La cantidad de entrega debe ser mayor a 0');
      return;
    }

    if (formData.forma_entrega === 'despacho' && !formData.direccion_despacho.trim()) {
      handleError('La dirección de despacho es obligatoria');
      return;
    }

    const monto_total = formData.cantidad * formData.precio_unitario;
    let montoPagado = venta?.monto_pagado ?? 0;
    if (!venta && formData.registrar_pago) montoPagado = formData.monto_pago_inicial;
    const errorImportes = validarImportesVentaActividad(monto_total,
      montoPagado,
      formData.cantidad,
      formData.registrar_entrega ? formData.cantidad_entrega_inicial : (venta?.cantidad_entregada ?? 0));
    if (errorImportes) {
      handleError(errorImportes);
      return;
    }

    if (formData.registrar_pago && formData.monto_pago_inicial > monto_total) {
      handleError('El monto de pago no puede ser mayor al monto total');
      return;
    }

    if (formData.registrar_entrega && formData.cantidad_entrega_inicial > formData.cantidad) {
      handleError('La cantidad de entrega no puede ser mayor a la cantidad solicitada');
      return;
    }

    if (pideComprobanteDelPago) {
      const errorComprobante = comprobante.validar();
      if (errorComprobante) {
        handleError(errorComprobante);
        return;
      }
    }

    clearError();
    setLoading(true);

    const camposCliente = limpiarCamposCliente(tipoCliente, {
      miembro_id: formData.miembro_id,
      nombre_externo: formData.nombre_externo,
      miembro_referido_id: formData.miembro_referido_id
    });

    try {
      if (venta) {
        const updateData = prepararCambiosPedidoActividad(formData, camposCliente);
        await ventaActividadService.update(venta.id, updateData);
        // El trigger de BD recalcula stock_actual automáticamente al cambiar cantidad
      } else {
        const respaldoPago = pideComprobanteDelPago ? await comprobante.resolver() : undefined;
        await ventaActividadService.crearPedido({
          actividad_id: actividadId,
          producto_id: formData.producto_id || undefined,
          produccion_id: formData.produccion_id || undefined,
          ...camposCliente,
          cantidad: formData.cantidad,
          monto_total,
          fecha_venta: formData.fecha_venta,
          modo_pago: comprobante.medioPago || 'efectivo',
          forma_entrega: formData.forma_entrega,
          fecha_entrega: formData.fecha_entrega || undefined,
          direccion_despacho: formData.forma_entrega === 'despacho' ? formData.direccion_despacho : undefined,
          rango_horario_retiro_despacho: formData.rango_horario_retiro_despacho || undefined,
          notas: formData.notas || undefined,
          registrar_pago: formData.registrar_pago,
          monto_pago_inicial: formData.monto_pago_inicial,
          fecha_pago: formData.fecha_pago,
          registrar_entrega: formData.registrar_entrega,
          cantidad_entrega_inicial: formData.cantidad_entrega_inicial,
          fecha_entrega_real: formData.fecha_entrega_real,
          respaldo_pago: respaldoPago
        });
      }

      // Leer los derivados confirmados por el contrato. después de registrar la venta

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'No pudimos guardar el pedido.');
    } finally {
      setLoading(false);
    }
  };

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  if (!isOpen) return null;

  const montoTotal = formData.cantidad * formData.precio_unitario;
  const hasUnsavedChanges = loading || error !== '' || hasFormChanges();

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      level={level}
      hasUnsavedChanges={hasUnsavedChanges}
      closeRef={closeRef}
      header={
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 rounded-t-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <ShoppingCart className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{venta ? 'Editar Pedido' : 'Registrar Pedido'}</h2>
                </div>
              </div>
              <button
                type="button"
                onClick={requestClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Indicador de progreso */}
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-2 ${pasoActual === 1 ? 'text-white' : 'text-emerald-200'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${pasoActual === 1 ? 'bg-white text-emerald-600' : 'bg-white/20'}`}>
                  1
                </div>
                <span className="text-sm font-medium">{venta ? 'Datos del Pedido' : 'Cliente y Producto'}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-emerald-200" />
              <div className={`flex items-center gap-2 ${pasoActual === 2 ? 'text-white' : 'text-emerald-200'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${pasoActual === 2 ? 'bg-white text-emerald-600' : 'bg-white/20'}`}>
                  2
                </div>
                <span className="text-sm font-medium">Entrega y Pago</span>
              </div>
            </div>
        </div>
      }
    >
        <form ref={formRef} onSubmit={handleSubmit} onKeyDown={(e) => {
          if (e.key === 'Enter' && pasoActual === 1) {
            e.preventDefault();
            irAlPaso2();
          }
        }} className="space-y-6">
          {/* Paso 1: Datos del pedido (compartido entre nuevo y edición) */}
          {pasoActual === 1 && (sinAsociacionMiembro || selectedLumisial) && (
            <Paso1ClienteProducto
              productos={productos}
              productoId={formData.producto_id}
              onProductoChange={handleProductoChange}
              precioUnitario={formData.precio_unitario}
              producciones={producciones}
              produccionId={formData.produccion_id}
              onProduccionChange={handleProduccionChange}
              tipoCliente={tipoCliente}
              onTipoClienteChange={setTipoCliente}
              miembroId={formData.miembro_id}
              onMiembroChange={(id, nombre) => {
                setFormData({ ...formData, miembro_id: id });
                if (nombre) {
                  setMiembroSeleccionado({ id, nombre });
                }
              }}
              nombreExterno={formData.nombre_externo}
              onNombreExternoChange={(nombre) => setFormData({ ...formData, nombre_externo: nombre })}
              miembroReferidoId={formData.miembro_referido_id}
              onMiembroReferidoChange={(id, nombre) => {
                setFormData({ ...formData, miembro_referido_id: id });
                if (nombre) {
                  setMiembroReferidoSeleccionado({ id, nombre });
                }
              }}
              lumisialId={selectedLumisial?.id ?? ''}
              initialMiembroNombre={initialMiembroNombre}
              initialMiembroReferidoNombre={initialMiembroReferidoNombre}
              cantidad={formData.cantidad}
              onCantidadChange={(cantidad) => setFormData({ ...formData, cantidad })}
              fechaVenta={formData.fecha_venta}
              onFechaVentaChange={(fecha) => setFormData({ ...formData, fecha_venta: fecha })}
              isEditMode={!!venta}
              sinAsociacionMiembro={sinAsociacionMiembro}
            />
          )}

          {/* Paso 2: Entrega y Pago (compartido entre nuevo y edición) */}
          {pasoActual === 2 && (
            <>
              {/* Resumen del pedido en paso 2 */}
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-emerald-700">Monto Total del Pedido</span>
                  <span className="text-2xl font-bold text-emerald-700">
                    {formatMoney(montoTotal)}
                  </span>
                </div>
                {venta && (
                  <div className="mt-2 pt-2 border-t border-emerald-200 grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-emerald-600">Pagado:</span>
                      <span className="font-semibold text-emerald-700">{formatMoney(formData.monto_pago_inicial)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-emerald-600">Entregado:</span>
                      <span className="font-semibold text-emerald-700">{formData.cantidad_entrega_inicial} de {formData.cantidad}</span>
                    </div>
                  </div>
                )}
              </div>

              <Paso2EntregaPago
                formaEntrega={formData.forma_entrega}
                onFormaEntregaChange={(forma) => setFormData({ ...formData, forma_entrega: forma, direccion_despacho: forma === 'retiro' ? '' : formData.direccion_despacho })}
                fechaEntrega={formData.fecha_entrega}
                onFechaEntregaChange={(fecha) => setFormData({ ...formData, fecha_entrega: fecha })}
                rangoHorario={formData.rango_horario_retiro_despacho}
                onRangoHorarioChange={(rango) => setFormData({ ...formData, rango_horario_retiro_despacho: rango })}
                direccionDespacho={formData.direccion_despacho}
                onDireccionDespachoChange={(direccion) => setFormData({ ...formData, direccion_despacho: direccion })}
                bloqueComprobante={venta ? undefined : (
                  <ComprobanteInput comprobante={comprobante} deshabilitado={loading} />
                )}
                bloqueMedioPago={<MedioPagoFolio comprobante={comprobante} deshabilitado={loading} />}
                notas={formData.notas}
                onNotasChange={(notas) => setFormData({ ...formData, notas })}
                isEditMode={!!venta}
                registrarPago={formData.registrar_pago}
                onRegistrarPagoChange={(registrar) => setFormData({ ...formData, registrar_pago: registrar, monto_pago_inicial: registrar ? (formData.monto_pago_inicial || montoTotal) : (venta ? venta.monto_pagado : 0) })}
                montoPagoInicial={formData.monto_pago_inicial}
                onMontoPagoInicialChange={(monto) => setFormData({ ...formData, monto_pago_inicial: monto })}
                fechaPago={formData.fecha_pago}
                onFechaPagoChange={(fecha) => setFormData({ ...formData, fecha_pago: fecha })}
                registrarEntrega={formData.registrar_entrega}
                onRegistrarEntregaChange={(registrar) => setFormData({ ...formData, registrar_entrega: registrar, cantidad_entrega_inicial: registrar ? (formData.cantidad_entrega_inicial || formData.cantidad) : (venta ? venta.cantidad_entregada : 0) })}
                cantidadEntregaInicial={formData.cantidad_entrega_inicial}
                onCantidadEntregaInicialChange={(cantidad) => setFormData({ ...formData, cantidad_entrega_inicial: cantidad })}
                fechaEntregaReal={formData.fecha_entrega_real}
                onFechaEntregaRealChange={(fecha) => setFormData({ ...formData, fecha_entrega_real: fecha })}
                montoTotal={montoTotal}
                cantidadTotal={formData.cantidad}
                precioUnitario={formData.precio_unitario}
              />
            </>
          )}

          {/* Error message */}
          <FormErrorAlert error={error} onClear={clearError} />

          {/* Botones de navegación */}
          <div className="flex gap-3 pt-4">
            {pasoActual === 2 && (
              <button
                type="button"
                onClick={volverAlPaso1}
                className="flex items-center gap-2 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
              >
                <ChevronLeft className="w-5 h-5" />
                Anterior
              </button>
            )}
            
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
            >
              Cancelar
            </button>
            
            {pasoActual === 1 ? (
              <button
                type="button"
                onClick={irAlPaso2}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold"
              >
                Siguiente
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading || comprobante.subiendo}
                className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Guardando...' : venta ? 'Actualizar' : 'Registrar'}
              </button>
            )}
          </div>
        </form>
    </DialogShell>
  );
};
